import { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { Select, useForm } from '@dansk-metal/ui';

import { Pagination } from '@web/components/pagination/pagination';

import { useAuthenticatedCRMRequestConfig } from '@web/services/crm-api/hooks';
import { GetCompanyMembersParams, MemberDto, useGetCompanyMembers } from '@web/services/crm-api/rest';

import { axiosParamsSerializer } from '@web/utils/axios-params-serializer';

// TODO: Remove when member scheme will be update with correct fields
/**
 *  Member Type mocks an ideal member object type that will return from the api.
 * 	Missing fields:
 * 		- position name. (positionId is returned but its not ideal to fetch every position name per member in the list)
 * 		- employer name. (company id is within the employer endpoint, but there is no way to fetch a company by id. company name could be included in schema for member)
 */
export type ExtendedMemberDto = MemberDto & { position: string; membershipNumber: string };

enum MembershipType {
	All = '',
	Akasse = 'isInAkasse',
	Union = 'isInUnion',
	Both = 'isBoth',
}

const memberShipOptions = [
	{ label: 'Alle medlemstyper', value: MembershipType.All },
	{ label: 'A-kasse', value: MembershipType.Akasse },
	{ label: 'Fagforening', value: MembershipType.Union },
	{ label: 'Fagforening og A-kasse', value: MembershipType.Both },
];

type MemberShipOption = typeof memberShipOptions[number];

interface UseMembersSearchFormProps {
	initialValue?: ExtendedMemberDto[];
	companyId: string;
	memberId: string;
	limit: number;
}

export function useMembersSearchForm({ initialValue, companyId, memberId, limit }: UseMembersSearchFormProps) {
	const { requestConfig } = useAuthenticatedCRMRequestConfig();
	const [total, setTotal] = useState(initialValue?.length || 0);
	const [nameSearchTerm, setNameSearchTerm] = useState('');
	const pagination = Pagination.usePagination({ limit, total });
	const form = useForm({ defaultValues: { name: '', membershipType: { } }, mode: 'onChange' });

	const [queryParams, setQueryParams] = useState<GetCompanyMembersParams>({
		pageSize: pagination.limit,
		page: pagination.page,
	});

	// Sync pagination to query params
	useEffect(() => {
		if (!window) return;
		setQueryParams((previousValue) => ({
			...previousValue,
			pageSize: pagination.limit,
			page: pagination.page,
		}));
		window.scrollTo(0, 0);
	}, [pagination.limit, pagination.page]);

	function setMembershipTypeValue({ value }: MemberShipOption) {
		if (value === MembershipType.All) {
			return form.setValue('membershipType', {});
		}

		if (value === MembershipType.Both) {
			return form.setValue('membershipType', { isInAkasse: true, isInUnion: true });
		}

		if (value === MembershipType.Akasse) {
			return form.setValue('membershipType', { isInAkasse: true, isInUnion: false });
		}

		if (value === MembershipType.Union) {
			return form.setValue('membershipType', { isInAkasse: false, isInUnion: true });
		}
	}

	const setNameSearchTextDebounced = useRef(
		debounce((searchText) => setNameSearchTerm(searchText), 100),
	).current;

	function handleSearchByName(event) {
		if (event.target.value.length >= 3 || event.target.value.length === 0) {
			setNameSearchTextDebounced(event.target.value);
		}

		form.setValue('name', event.target.value);
	}

	const membershipTypeSelect = {
		options: memberShipOptions,
		...Select.useSelect<MemberShipOption, false>({
			isMulti: false,
			onChange: setMembershipTypeValue,
			initialSelect: memberShipOptions[0],
		}),
	};

	const filterQuery = useGetCompanyMembers(companyId, queryParams, {
		query: {
			enabled: !!companyId,
		},
		request: {
			...requestConfig,
			paramsSerializer: {
				serialize: (params) => axiosParamsSerializer(params),
			},
		},
	});

	useDeepCompareEffect(() => {
		if (companyId) {
			filterQuery.refetch();
		}
	}, [filterQuery.refetch, queryParams, companyId]);

	useEffect(() => {
		if (typeof filterQuery.data?.meta?.total === 'number') {
			setTotal(filterQuery.data.meta.total);
		}
	}, [filterQuery.data, filterQuery.refetch, companyId]);

	const submit = useCallback(async () => {
		const membershipType = form.getValues('membershipType');

		setQueryParams(() => ({
			memberId,
			pageSize: pagination.limit,
			search: nameSearchTerm,
			...membershipType,
		}));
	// eslint-disable-next-line react-hooks/exhaustive-deps -- we cannot use form as a dependency as it will cause an infinite loop since the form is updated with a rerender of the submit
	}, [companyId, nameSearchTerm, form.getValues('membershipType')]);

	return {
		...form,
		query: filterQuery,
		pagination,
		submit,
		membershipTypeSelect,
		handleSearchByName,
		total,
	};
}
